import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import { ConversationProgress, ConversationFeedback, ConversationAnalytics } from '@/services/conversation';

// Async actions for saga
export const fetchSessionProgress = createAction<{ sessionId: string }>('conversationProgress/fetchSessionProgress');
export const fetchSessionProgressSuccess = createAction<ConversationProgress>(
    'conversationProgress/fetchSessionProgressSuccess',
);
export const fetchSessionProgressFailure = createAction<string>('conversationProgress/fetchSessionProgressFailure');

export const updateSessionProgress = createAction<{ sessionId: string; progressData: Partial<ConversationProgress> }>(
    'conversationProgress/updateSessionProgress',
);
export const updateSessionProgressSuccess = createAction<ConversationProgress>(
    'conversationProgress/updateSessionProgressSuccess',
);
export const updateSessionProgressFailure = createAction<string>('conversationProgress/updateSessionProgressFailure');

export const fetchSessionFeedback = createAction<{ sessionId: string }>('conversationProgress/fetchSessionFeedback');
export const fetchSessionFeedbackSuccess = createAction<ConversationFeedback>(
    'conversationProgress/fetchSessionFeedbackSuccess',
);
export const fetchSessionFeedbackFailure = createAction<string>('conversationProgress/fetchSessionFeedbackFailure');

export const generateSessionFeedback = createAction<{ sessionId: string }>(
    'conversationProgress/generateSessionFeedback',
);
export const generateSessionFeedbackSuccess = createAction<ConversationFeedback>(
    'conversationProgress/generateSessionFeedbackSuccess',
);
export const generateSessionFeedbackFailure = createAction<string>(
    'conversationProgress/generateSessionFeedbackFailure',
);

export const fetchSessionAnalytics = createAction<{ sessionId: string }>('conversationProgress/fetchSessionAnalytics');
export const fetchSessionAnalyticsSuccess = createAction<ConversationAnalytics>(
    'conversationProgress/fetchSessionAnalyticsSuccess',
);
export const fetchSessionAnalyticsFailure = createAction<string>('conversationProgress/fetchSessionAnalyticsFailure');

export interface ConversationProgressState {
    currentProgress: ConversationProgress | null;
    currentFeedback: ConversationFeedback | null;
    currentAnalytics: ConversationAnalytics | null;
    progressHistory: ConversationProgress[];
    isLoading: boolean;
    isUpdating: boolean;
    isGeneratingFeedback: boolean;
    error: string | null;
    lastUpdated: string | null;
}

const initialState: ConversationProgressState = {
    currentProgress: null,
    currentFeedback: null,
    currentAnalytics: null,
    progressHistory: [],
    isLoading: false,
    isUpdating: false,
    isGeneratingFeedback: false,
    error: null,
    lastUpdated: null,
};

const conversationProgressSlice = createSlice({
    name: 'conversationProgress',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetState: () => initialState,
        setCurrentProgress: (state, action: PayloadAction<ConversationProgress>) => {
            state.currentProgress = action.payload;
        },
        setCurrentFeedback: (state, action: PayloadAction<ConversationFeedback>) => {
            state.currentFeedback = action.payload;
        },
        setCurrentAnalytics: (state, action: PayloadAction<ConversationAnalytics>) => {
            state.currentAnalytics = action.payload;
        },
        clearCurrentSession: (state) => {
            state.currentProgress = null;
            state.currentFeedback = null;
            state.currentAnalytics = null;
        },
        addToProgressHistory: (state, action: PayloadAction<ConversationProgress>) => {
            const existingIndex = state.progressHistory.findIndex((progress) => progress.id === action.payload.id);
            if (existingIndex >= 0) {
                state.progressHistory[existingIndex] = action.payload;
            } else {
                state.progressHistory.unshift(action.payload);
            }

            // Keep only latest 20 progress records
            if (state.progressHistory.length > 20) {
                state.progressHistory = state.progressHistory.slice(0, 20);
            }
        },
        updateProgressMetrics: (
            state,
            action: PayloadAction<{
                total_questions_answered?: number;
                correct_answers?: number;
                speaking_accuracy?: string;
                reading_accuracy?: string;
                vocabulary_mastery?: string;
                total_conversation_time?: string;
            }>,
        ) => {
            if (state.currentProgress) {
                state.currentProgress = {
                    ...state.currentProgress,
                    ...action.payload,
                    updated_at: new Date().toISOString(),
                };
            }
        },
    },
    extraReducers: (builder) => {
        // Fetch Session Progress
        builder
            .addCase(fetchSessionProgress, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSessionProgressSuccess, (state, action) => {
                state.isLoading = false;
                state.currentProgress = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;

                // Add to history
                const existingIndex = state.progressHistory.findIndex((progress) => progress.id === action.payload.id);
                if (existingIndex >= 0) {
                    state.progressHistory[existingIndex] = action.payload;
                } else {
                    state.progressHistory.unshift(action.payload);
                }
            })
            .addCase(fetchSessionProgressFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Update Session Progress
        builder
            .addCase(updateSessionProgress, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateSessionProgressSuccess, (state, action) => {
                state.isUpdating = false;
                state.currentProgress = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;

                // Update in history
                const existingIndex = state.progressHistory.findIndex((progress) => progress.id === action.payload.id);
                if (existingIndex >= 0) {
                    state.progressHistory[existingIndex] = action.payload;
                }
            })
            .addCase(updateSessionProgressFailure, (state, action) => {
                state.isUpdating = false;
                state.error = action.payload;
            });

        // Fetch Session Feedback
        builder
            .addCase(fetchSessionFeedback, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSessionFeedbackSuccess, (state, action) => {
                state.isLoading = false;
                state.currentFeedback = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchSessionFeedbackFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });

        // Generate Session Feedback
        builder
            .addCase(generateSessionFeedback, (state) => {
                state.isGeneratingFeedback = true;
                state.error = null;
            })
            .addCase(generateSessionFeedbackSuccess, (state, action) => {
                state.isGeneratingFeedback = false;
                state.currentFeedback = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(generateSessionFeedbackFailure, (state, action) => {
                state.isGeneratingFeedback = false;
                state.error = action.payload;
            });

        // Fetch Session Analytics
        builder
            .addCase(fetchSessionAnalytics, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSessionAnalyticsSuccess, (state, action) => {
                state.isLoading = false;
                state.currentAnalytics = action.payload;
                state.lastUpdated = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchSessionAnalyticsFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearError,
    resetState,
    setCurrentProgress,
    setCurrentFeedback,
    setCurrentAnalytics,
    clearCurrentSession,
    addToProgressHistory,
    updateProgressMetrics,
} = conversationProgressSlice.actions;

export default conversationProgressSlice.reducer;
